import calculateField, {
  calculateFromTotals,
  renderFormula,
} from './calculateFields';
import { isValue } from '../../../utility/isValue';
import FormUIOptions from '../formElements/formUIOptions';
import {
  formatLargeNumber,
  getNumberWithCommas,
} from '../../../utility/formatNumbers';
import convertUnitMoney from '../../../utility/convertUnitMoney';

export default function renderValue(field, data, ...args) {
  const value = data[field.name];
  switch (field.type) {
    case FormUIOptions.Text:
    case FormUIOptions.TextArea:
    case FormUIOptions.Explain:
      return value;

    case FormUIOptions.YesNo:
      return value ? field.yesLabel || 'Yes' : field.noLabel || 'No';

    case FormUIOptions.Select:
      return field.options.find((opt) => opt.value === value)
        ? field.options.find((opt) => opt.value === value).label
        : 'N/A';

    case FormUIOptions.Number: {
      const number =
        field.unit === 'years' || value === Infinity || value === -Infinity
          ? value
          : getNumberWithCommas(
              value,
              isValue(field.decimalPlaces) ? field.decimalPlaces : 2
            );
      const newValue = number
        ? `${number}${field.unit === 'years' ? ' ' : ''}${field.unit || ''}`
        : '';
      return field.formula ? renderFormula(field, newValue) : newValue;
    }

    case FormUIOptions.Money: {
      let newValue = value
        ? `$${getNumberWithCommas(
            value,
            isValue(field.decimalPlaces) ? field.decimalPlaces : 2
          )}`
        : value === 0
        ? '$0.00'
        : ' - ';
      newValue = newValue.replace('$-', '-$');
      return field.formula ? renderFormula(field, newValue) : newValue;
    }

    case FormUIOptions.Calculated:
      const result = calculateField(field, data);
      const formatted = getNumberWithCommas(
        result,
        isValue(field.decimalPlaces) ? field.decimalPlaces : 2
      );
      return field.unit === '$'
        ? `$${formatted}`
        : `${formatted}${field.unit || ''}`;

    case FormUIOptions.CalculatedTable:
      return calculateFromTotals(field, ...args);

    case FormUIOptions.UnitMoney:
      //some fields are deleted and we need to return empty string
      if (data[field.name] === 'DELETED') return '';
      //normal unit money field
      const amount = data[field.name]?.value;
      const unit = data[field.name]?.unit;
      switch (unit) {
        case 'yearly': {
          return amount || amount === 0
            ? `$${getNumberWithCommas(amount, 2)}/year`
            : '';
        }
        case 'monthly': {
          return amount || amount === 0
            ? `$${getNumberWithCommas(amount, 2)}/month`
            : '';
        }
        case '%': {
          if (amount || amount === 0) {
            const value = convertUnitMoney(data[field.name], data) || 0;
            return `$${getNumberWithCommas(value, 2)}`;
          }
          return '';
        }
        case 'amount':
        default: {
          return amount || amount === 0
            ? `$${getNumberWithCommas(amount, 2)}`
            : '';
        }
      }

    default:
      return value;
  }
}

export function formatValue(field, data) {
  const value = data[field.name];

  switch (field.type) {
    case FormUIOptions.Text:
    case FormUIOptions.TextArea:
    case FormUIOptions.Explain:
      return value;

    case FormUIOptions.YesNo:
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      return 'N/A';

    case FormUIOptions.Select:
      return field.options.find((opt) => opt.value === value)
        ? field.options.find((opt) => opt.value === value).label
        : 'N/A';

    case FormUIOptions.Number:
      const number = getNumberWithCommas(value, 2);
      return number ? `${number}${field.unit || ''}` : '';

    case FormUIOptions.Money:
      return value
        ? `$${getNumberWithCommas(value, 2)}`
        : value === 0
        ? '$0.00'
        : ' - ';

    case FormUIOptions.Calculated:
      const calculatedResult =
        field.unit === '$' ? `$${value}` : `${value}${field.unit || ''}`;
      return field.formula
        ? renderFormula(field, data, calculatedResult)
        : calculatedResult;

    case FormUIOptions.UnitMoney:
      const amount = value?.value;
      const unit = value?.unit;
      switch (unit) {
        case '%': {
          return amount ? `${amount}%` : '';
        }
        case 'yearly':
        case 'monthly':
        case 'amount':
        default: {
          return amount ? `$${getNumberWithCommas(amount, 2)}` : '';
        }
      }

    default:
      return value;
  }
}

export function formatKeyMetrics(field, data = {}) {
  const value = data[field.name];
  const abs = field.absoluteValue;

  switch (field.type) {
    case FormUIOptions.Number:
      const number = getNumberWithCommas(
        value,
        isValue(field.decimals) ? field.decimals : 2
      );
      return number ? `${number}${field.unit || ''}` : '-'; // "-" is to maintain line height if value is empty

    case FormUIOptions.Money:
      if (value || value === 0) {
        const displayNum = `${formatLargeNumber(value, field)}`;
        return !abs
          ? displayNum.replace('$-', '-$')
          : displayNum.replace('-', '');
      }
      return '-';

    case FormUIOptions.UnitMoney:
      const amount = value?.value;
      const unit = value?.unit;
      switch (unit) {
        case '%': {
          return amount ? `${getNumberWithCommas(amount)}%` : '-';
        }
        case 'yearly':
        case 'monthly':
        case 'amount':
        default: {
          return amount ? `${formatLargeNumber(amount, 2)}` : '-';
        }
      }

    case FormUIOptions.Calculated:
      return value ? value : '-';

    default:
      return value;
  }
}

export function renderMoneyAmount(amount) {
  if (!amount || !isFinite(amount)) return null;

  if (amount === 0) return '$0.00';

  if (amount < 0) {
    return `-$${getNumberWithCommas(Math.abs(amount), 2)}`;
  }

  return `$${getNumberWithCommas(amount, 2)}`;
}

export function renderMoneyAmountShort(amount, showPositiveSign = false) {
  if (amount === null || amount === undefined || !isFinite(amount)) return null;

  if (amount === 0) return '$0.00';

  if (amount < 0) {
    return `-$${formatLargeNumber(Math.abs(amount))}`;
  }

  if (showPositiveSign) {
    return `+$${formatLargeNumber(amount)}`;
  }

  return `$${formatLargeNumber(amount)}`;
}
